import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import type React from "react";
import { useSWRConfig } from "swr";
import { StatusHandleButtons } from "~/components/common/statuses/StatusHandleButtons";
import { useDealContext } from "~/context/DealContext";
import { useDealTodos } from "~/hooks/deals/useDealTodos";
import { dealCacheKey } from "~/hooks/useDeals";
import useLoader from "~/hooks/useLoader";
import { useTodoTemplates } from "~/hooks/useTodoTemplates";
import { PARTIALLY_FINISHED } from "~/models/Status";
import {
	countUncreatedByTemplates,
	filterTodoTemplatesByStatus
} from "~/models/TodoTemplate";
import { isSuccess } from "~/utils/api/api_handler";
import { update as updateStatus } from "~/utils/api/DealStatus";
import { create as createDealEstimationHeader } from "~/utils/api/deals/DealOwnEstimationHeader";

type Props = {
	allowPartiallyFinish?: boolean;
	backwardDisabled: boolean;
	forwardDisabled: boolean;
	nextStatusId: number;
	prevStatusId?: number | null;
};

export const OwnStatusHandleButtons: React.FC<Props> = ({
	nextStatusId,
	prevStatusId = null,
	forwardDisabled,
	backwardDisabled,
	allowPartiallyFinish = false
}: Props) => {
	const { notify } = useNotification();
	const { Loader, showLoader, hideLoader } = useLoader();
	const deal = useDealContext();
	const { mutate: globalMutate } = useSWRConfig();
	const todoTemplates = useTodoTemplates().useIndex().data;
	const dealTodos = useDealTodos(deal.id);

	if (todoTemplates === null || dealTodos.data === null) {
		return null;
	}

	const uncreatedTodoCount = countUncreatedByTemplates(
		dealTodos.data,
		filterTodoTemplatesByStatus(deal, nextStatusId, todoTemplates)
	);
	const handleUpdateStatus = async (statusId: number): Promise<void> => {
		showLoader();
		const result = await updateStatus(deal.id, statusId, null, null);
		if (isSuccess(result)) {
			await globalMutate(dealCacheKey(deal.id));
			hideLoader();
			notify({ content: `ステータスを更新しました。`, variant: "success" });
			return;
		}
		hideLoader();
		notify({ content: "エラーが発生しました。", variant: "error" });
	};
	const handleForwardStatus = async (): Promise<void> => {
		const result = await createDealEstimationHeader(deal.id);
		if (isSuccess(result)) {
			await handleUpdateStatus(nextStatusId);
			await dealTodos.mutate();
			if (uncreatedTodoCount !== 0) {
				notify({
					content: `${uncreatedTodoCount}件のTODOを追加しました。`,
					variant: "success"
				});
			}
			return;
		}
		notify({ content: "エラーが発生しました。", variant: "error" });
	};
	const handleBackwardStatus = async (): Promise<void> => {
		if (prevStatusId === null) return;
		await handleUpdateStatus(prevStatusId);
	};
	const handlePartiallyFinish = async (): Promise<void> => {
		if (!allowPartiallyFinish) return;
		handleUpdateStatus(PARTIALLY_FINISHED);
	};
	return (
		<>
			<Loader />
			<StatusHandleButtons
				allowPartiallyFinish={allowPartiallyFinish}
				backwardDisabled={backwardDisabled}
				canBackward={prevStatusId !== null}
				forwardDisabled={forwardDisabled}
				handleBackwardStatus={handleBackwardStatus}
				handleForwardStatus={handleForwardStatus}
				handlePartiallyFinish={handlePartiallyFinish}
			/>
		</>
	);
};
